'use client';

import { createContext, type ReactNode, type TransitionStartFunction, useTransition } from 'react';
import type { BranchId } from '../../../config/constants';
import type { FlattenedFunnelConfig } from '../../../config/flattened/types';
import { useFunnelSessionSync } from '../../answer/session/useFunnelSessionSync';
import type { FunnelStepSnapshot } from '../stepSnapshot';
import { useHandleLanding } from './hooks';

export type FunnelContextValue = {
  funnelFlattenedConfig: FlattenedFunnelConfig;
  stepId: string;
  branchId: BranchId | null;
  isFirstStep: boolean;
  isBranchFirstStep: boolean;
  isBranchLastStep: boolean;
  progress: number;
  funnelConfigVersion: string;
  isSubmitting: boolean;
  startSubmitTransition: TransitionStartFunction;
};

export const FunnelContext = createContext<FunnelContextValue | undefined>(undefined);

type FunnelProviderProps = {
  children: ReactNode;
  funnelFlattenedConfig: FlattenedFunnelConfig;
  stepSnapshot: FunnelStepSnapshot;
  branchId: BranchId | null;
  funnelConfigVersion: string;
};

export function FunnelProvider({
  children,
  funnelFlattenedConfig,
  stepSnapshot,
  branchId,
  funnelConfigVersion,
}: FunnelProviderProps) {
  const { stepId, isFirstStep, isBranchFirstStep, isBranchLastStep, progress } = stepSnapshot;
  useHandleLanding({ funnelFlattenedConfig, stepSnapshot, branchId });
  const [isSubmitting, startSubmitTransition] = useTransition();
  useFunnelSessionSync(branchId, funnelConfigVersion, startSubmitTransition);

  const value: FunnelContextValue = {
    funnelFlattenedConfig,
    stepId,
    branchId,
    isFirstStep,
    isBranchFirstStep,
    isBranchLastStep,
    progress,
    funnelConfigVersion,
    isSubmitting,
    startSubmitTransition,
  };

  return <FunnelContext.Provider value={value}>{children}</FunnelContext.Provider>;
}
